import { Form } from 'antd';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Colors } from 'basic-ui-lib';
import { useEffect, useState } from 'react';
import EditorHeader from '../EditorHeader';
import DevicePanel from '../DevicePanel';
import { LayoutCode } from 'modules/channel/services/constants';
import { IDevice, IScreenDetail } from 'modules/dashboard/interfaces/api';
import { ScreenStatus } from 'modules/dashboard/services/constants';
import dashboardApis from 'modules/dashboard/services/apis';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import { IContentItem } from 'modules/playlist/interfaces/api';
import ZoneListPlaylist from '../ZoneListPlaylist';

export interface ScreenFormValues {
  layoutCode: LayoutCode;
  storeId: string;
  screenName: string;
  contentItems: IContentItem[];
  activeZone?: string;
  deviceId: string;
  playListId: string;
}

interface LocationState {
  storeId?: string;
  screenName?: string;
  deviceInfo?: IDevice;
}

const HARDCODED_NOTE = 'Screen created from dashboard';

interface ScreenFormProps {
  screenId?: string;
}

const ScreenForm = ({ screenId }: ScreenFormProps) => {
  const [form] = Form.useForm<ScreenFormValues>();
  const location = useLocation();
  const navigate = useNavigate();

  const [screenDetail, setScreenDetail] = useState<IScreenDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { storeId, screenName, deviceInfo: createDeviceInfo } = (location.state as LocationState) || {};
  
  const isEditMode = Boolean(screenId && screenId !== 'create');
  const currentStatus = screenDetail?.status as ScreenStatus | undefined;
  const deviceInfo = isEditMode ? screenDetail?.deviceInfo : createDeviceInfo;

  useEffect(() => {
    if (isEditMode && screenId) {
      const fetchDetail = async () => {
        setIsLoading(true);
        try {
          const res = await dashboardApis.getScreenDetail(screenId);
          if (res?.data?.data) {
            const detail = res.data.data as IScreenDetail;
            setScreenDetail(detail);
            
            form.setFieldsValue({
              storeId: detail.storeId ?? undefined,
              playListId: detail.playListId ?? undefined,
              layoutCode: detail.layoutCode as LayoutCode,
              contentItems: detail.contentItems,
              deviceId: detail.deviceInfo?.deviceId,
              screenName: detail.screenName,
            });
          }
        } catch (error) {
          toast.error(getMessageError(error, 'Failed to load screen details'));
        } finally {
          setIsLoading(false);
        }
      };
      fetchDetail();
    }
  }, [isEditMode, screenId, form]);

  const buildCreatePayload = (status: ScreenStatus): Parameters<typeof dashboardApis.createScreen>[0] => {
    const values = form.getFieldsValue();
    return {
      deviceId: values.deviceId || createDeviceInfo?.id || '',
      storeId: values.storeId || '',
      screenName: values.screenName || '',
      playListId: values.playListId || '',
      status: status,
      note: HARDCODED_NOTE,
      contentItems: values.contentItems || [],
      layoutCode: values.layoutCode || LayoutCode.MAIN_ONLY,
      duration: values.contentItems
        ? values.contentItems.reduce((sum, item) => sum + (item.duration || 0), 0)
        : 0,
    };
  };

  const buildUpdatePayload = (status: ScreenStatus) => {
    const values = form.getFieldsValue();
    return {
      id: screenId || '',
      deviceId: values.deviceId || screenDetail?.deviceInfo?.deviceId || '',
      storeId: values.storeId,
      screenName: values.screenName,
      playListId: values.playListId,
      layoutCode: values.layoutCode,
      status: status,
      note: 'Updated from dashboard',
      duration: values.contentItems
        ? values.contentItems.reduce((sum, item) => sum + (item.duration || 0), 0)
        : screenDetail?.duration || 0,
      contentItems: values.contentItems,
    };
  };

  const handleSaveDraft = async () => {
    try {
      const payload = buildCreatePayload(ScreenStatus.DRAFT);
      const res = await dashboardApis.createScreen(payload);
      if (res?.data?.data) {
        toast.success('Screen saved as draft');
        navigate('/screen');
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to save draft'));
    }
  };

  const handlePublish = async () => {
    try {
      if (!isEditMode) {
        const payload = buildCreatePayload(ScreenStatus.ONLINE);
        const res = await dashboardApis.createScreen(payload);
        if (res?.data?.data) {
          toast.success('Screen published successfully');
          navigate('/screen');
        }
      } else if (screenId) {
        const payload = buildUpdatePayload(ScreenStatus.ONLINE);
        const res = await dashboardApis.updateScreen(payload);
        if (res?.data?.data) {
          toast.success('Screen published successfully');
          setScreenDetail(prev => prev ? { ...prev, status: ScreenStatus.ONLINE } : null);
        }
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to publish screen'));
    }
  };

  const handleUpdate = async () => {
    try {
      if(!isEditMode) return;

      const payload = buildUpdatePayload(currentStatus || ScreenStatus.DRAFT);
      const res = await dashboardApis.updateScreen(payload);
      if (res?.data?.data) {
        toast.success('Screen updated successfully');
        setScreenDetail(prev => prev ? { ...prev, status: currentStatus || ScreenStatus.DRAFT } : null);
        navigate('/screen');
      } 
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to update screen'));
    }
  };

  const handleDeactivate = async () => {
    if (!screenId) return;
    try {
      const res = await dashboardApis.updateStatusScreen({ id: screenId, status: ScreenStatus.DEACTIVATED });
      if (res?.data?.data) {
        toast.success('Screen deactivated successfully');
        setScreenDetail(prev => prev ? { ...prev, status: ScreenStatus.DEACTIVATED } : null);
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to deactivate screen'));
    }
  };

  const handlePause = async () => {
    if (!screenId) return;
    try {
      const res = await dashboardApis.updateStatusScreen({ id: screenId, status: ScreenStatus.OFFLINE });
      if (res?.data?.data) {
        toast.success('Screen paused successfully');
        setScreenDetail(prev => prev ? { ...prev, status: ScreenStatus.OFFLINE } : null);
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to pause screen'));
    }
  };

  const handleReactivate = async () => {
    if (!screenId) return;
    try {
      const res = await dashboardApis.updateStatusScreen({ id: screenId, status: ScreenStatus.ONLINE });
      if (res?.data?.data) {
        toast.success('Screen reactivated successfully');
        setScreenDetail(prev => prev ? { ...prev, status: ScreenStatus.ONLINE } : null);
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to reactivate screen'));
    }
  };
  
  return (
    <Container>
      <Form 
        form={form} 
        layout="vertical" 
        className="screen-form"
        initialValues={!isEditMode ? {
          layoutCode: LayoutCode.MAIN_ONLY,
          storeId: storeId,
          screenName: screenName,
          deviceId: createDeviceInfo?.id,
        } : undefined}
        onFinish={handlePublish}
      >
        <Form.Item name="deviceId" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="layoutId" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="storeId" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="channelId" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="playListId" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="screenName" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="contentItems" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="activeZone" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="layoutCode" hidden>
          <input type="hidden" />
        </Form.Item>
        <LeftSection>
          <EditorHeader 
            form={form} 
            screenId={screenId} 
            screenStatus={currentStatus} 
            isCreateMode={!isEditMode} 
            onSaveDraft={handleSaveDraft}
            onDeactivate={handleDeactivate}
            onPause={handlePause}
            onReactivate={handleReactivate}
            onUpdate={handleUpdate}
          />
          <ZoneListPlaylist />
          {/* <ZoneCanvasSection form={form} /> */}
        </LeftSection>
        
        <DevicePanel 
          isCreateMode={!isEditMode}
          screenId={screenDetail?.id}
          screenStatus={currentStatus}
          deviceInfo={deviceInfo}
          isLoading={isLoading}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onDeactivate={handleDeactivate}
          onPause={handlePause}
          onReactivate={handleReactivate}
        />
      </Form>
    </Container>
  );
};

export default ScreenForm;

const Container = styled.div`
  display: flex;
  height: calc(100vh - 60px);
  overflow: hidden;

  .screen-form {
    display: flex;
    width: 100%;
    height: 100%;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${Colors.neutral.bg[1]};
  min-width: 0;
`;
